import {
  Filters,
  InitialFieldsAlignment,
} from "@type/core/dashboard/dashboard.types";
import {
  QcpSignatureLevelAvailable,
  SignatureLevelAvailable,
  SignatureRequirementMode,
  TiersSignatureLevelAvailable,
  TransactionStatus,
} from "@type/core/transaction/transaction.types";
import DateFormat from "@utils/DateFormat";
import { AlertPreferenceDelayTypeEnum } from "@utils/enums/alert.preference.delay.type.enum";
import { AlertPreferencesType } from "@utils/enums/alert.preferences.type.enum";
import { Rule } from "antd/es/form";
import i18n, { t } from "i18next";
import { RegexHelper } from "@utils/helpers/regex-helper";
import { toastError } from "@utils/helpers/toast-helper";

export const formValidateTriggers = {
  validateTrigger: ["onBlur"],
};
export const formHorizontalLayout = {
  labelCol: { span: 24, md: 8 },
  wrapperCol: { span: 24, md: 16 },
};
export const formRules = {
  email: (skipValidation) => [
    {
      validator: (_, value) => {
        if (skipValidation) {
          return Promise.resolve();
        }

        const typedValue = String(value ?? "");
        if (RegexHelper.checkEmail(typedValue)) {
          return Promise.resolve();
        } else {
          toastError(
            t(
              typedValue === ""
                ? "error.form.mandatoryEmail"
                : "error.form.emailFormat",
            ),
          );
          return Promise.reject();
        }
      },
    },
  ],
  phone: (skipValidation) => [
    {
      validator: (_, value) => {
        if (skipValidation) {
          return Promise.resolve();
        }

        const typedValue = String(value ?? "");
        if (RegexHelper.checkPhone(typedValue)) {
          return Promise.resolve();
        } else {
          toastError(
            t(
              typedValue === ""
                ? "error.form.mandatoryPhone"
                : "error.form.phoneFormat",
            ),
          );
          return Promise.reject();
        }
      },
    },
  ],
} satisfies Record<string, (skipValidation?: boolean) => Rule[]>;

export const DEFAULT_500_ERROR = {
  responseCode: 500,
  errorMessage: i18n.t("error.500"),
  data: undefined,
  ok: false,
};

export const emptySearchResult = {
  numberOfElements: 0,
  totalElements: 0,
  size: 0,
  totalPages: 0,
  number: 0,
  content: [],
};

export const FILTER_TYPE_DROPDOWN = ["signature", "scellement"];

export const UNIVERSIGN_RATIO = 1;

export const MAX_SIGNATURE_AMOUNT = 60;

export const FEATURE_HAS_BEEN_TESTED_AND_SHOULD_APPEAR = false;

export const U2_CARD = {
  cardWidth: 200,
  cardHeight: 50,
};

export const CANCELED_CAUSE = {
  REVOCATION:
    "Un certificat d'un signataire n'ayant pas encore signé a été révoqué.",
};

export const TRANSACTION_STATUS_ENUM: {
  [key: string]: TransactionStatus | string[];
} = {
  DRAFT: ["draft", "<span class='text-muted'>Brouillon</span>"],
  PREPARED: ["prepared", "<span class='text-muted'>Préparé</span>"],
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IN_PROGRESS: [
    "inProgress",
    "<span class='text-warning'>En cours de signature</span>",
  ],
  FINISHED: ["finished", "<span class='text-success'>Terminé</span>"],
  CLASSIFIED: ["classified", "<span>Classé</span>"],
  REFUSED: ["refused", "<em class='text-danger'>Refusé</em>"],
  CANCELED: ["canceled", "<em>Annulé</em>"],
  EXPIRED: ["expired", "<em class='text-danger'>Expiré</em>"],
  PENDING: ["pending", "<em>En cours de clôture</em>"],
  ERROR: ["error", "Erreur"],
};

export const SIGNATORY_STATUS_ENUM: { [key: string]: string[] } = {
  WAITING: ["waiting", "text-warning"],
  SIGNED: ["signed", "text-success"],
  REFUSED: ["refused", "text-danger"],
  CANCELED: ["canceled", "text-danger"],
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IN_PROGRESS: ["inProgress", "text-warning"],
  ERROR: ["error", "text-danger"],
  SIMULTANEOUS: ["simultaneous", "text-warning"],
};

export const FILTER_STATUS_DROPDOWN = [
  TRANSACTION_STATUS_ENUM.DRAFT[0],
  TRANSACTION_STATUS_ENUM.PREPARED[0],
  TRANSACTION_STATUS_ENUM.IN_PROGRESS[0],
  TRANSACTION_STATUS_ENUM.EXPIRED[0],
  TRANSACTION_STATUS_ENUM.FINISHED[0],
  TRANSACTION_STATUS_ENUM.REFUSED[0],
  TRANSACTION_STATUS_ENUM.CANCELED[0],
  TRANSACTION_STATUS_ENUM.PENDING[0],
  TRANSACTION_STATUS_ENUM.CLASSIFIED[0],
];

export const DATES_FILTER = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  LAST_MODIFICATION: "lastModificationDate",
  EXPIRATION: "expirationDate",
};

export const EDITOR_TOKEN_TYPE = {
  USER: "user",
  EDITOR: "office",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  EDITOR_USER: "officeAndUser",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ALL_EDITOR_USER: "officesAndUser",
};

export const initialFilters: Filters = {
  cabinet: [],
  status: FILTER_STATUS_DROPDOWN.filter(
    (status) => status !== TRANSACTION_STATUS_ENUM.CLASSIFIED[0],
  ),
  signatureCheck: true,
  scellementCheck: true,
  selectedDateButton: DATES_FILTER.LAST_MODIFICATION,
  datePeriod: {
    beginPeriod: DateFormat.initialDashboardStartPeriodDate(),
    endPeriod: DateFormat.initialDashboardEndPeriodDate(),
  },
  search: "",
};

export const ALERT_PERIODICITY_DAYS: {
  [key: string]: { label: string; className: string };
} = {
  sendOnMonday: {
    label: "Lundi",
    className: "ml-16",
  },
  sendOnTuesday: {
    label: "Mardi",
    className: "ml-16",
  },
  sendOnWednesday: {
    label: "Mercredi",
    className: "ml-16",
  },
  sendOnThursday: {
    label: "Jeudi",
    className: "ml-16",
  },
  sendOnFriday: {
    label: "Vendredi",
    className: "ml-16",
  },
  sendOnSaturday: {
    label: "Samedi",
    className: "ml-16",
  },
  sendOnSunday: {
    label: "Dimanche",
    className: "ml-16",
  },
};

export const DISPLAYED_PREFERENCES: AlertPreferencesType[] = [
  AlertPreferencesType.COLLECTION_PREPARED,
  AlertPreferencesType.TRANSACTION_SENT,
  AlertPreferencesType.SIGNATURE_PERFORMED,
  AlertPreferencesType.COLLECTION_PAUSED,
  AlertPreferencesType.EXPECTED_EXPIRATION,
  AlertPreferencesType.COLLECTION_EXPIRED,
  AlertPreferencesType.COLLECTION_RELAUNCHED,
  AlertPreferencesType.SIGNATURES_COLLECTION_DONE,
  AlertPreferencesType.SIGNATURE_REFUSED,
  AlertPreferencesType.SEALING_COLLECTION_DONE,
  AlertPreferencesType.SEALING_ERROR,
  AlertPreferencesType.EXPECTED_CLEAN_UP,
  AlertPreferencesType.NOT_DOWNLOADED_DOCUMENT,
  AlertPreferencesType.LOW_ACCOUNT,
  AlertPreferencesType.ACCOUNT_CREDITED,
  AlertPreferencesType.CONFIDENTIALITY_PROPERTY_REMOVED,
  AlertPreferencesType.DEGRADED_MODE,
];

export const ALERT_LOW_CREDIT_ENUM = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 25, 30, 35, 40, 45, 50, 60,
  70, 80, 90, 100,
];

export const ALERT_DAYS_ENUM = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

export const ALERT_DAYS_ENUM_SHORT = [1, 3, 5, 7, 14, 30];

export const DISPLAY_PROMO_CODE_BUTTON = false;

export const SEALED_STATUS_ENUM = [
  "FILE_SEALED",
  "FILE_SENT_BY_MAIL",
  "MAIL_ERROR",
  "ACCOUNTING_DONE",
];

export const MAIL_DEFAULT_SUBJECT = {
  INVITATION: "Vous êtes invité à signer des documents",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  END_OF_TRANSACTION: "Service de signature",
};

export const LOCAL_STORAGE = {
  COOKIE: "COOKIE",
};

export const CALLBACK_URL_PARAM = "c";

export const IMG_DEFAULT_ALT = "";
export const LINK_DEFAULT_TITLE = "";

export const MAX_SIZE_ALL_FILES = 25 * 1024 * 1024;
export const MAX_SIZE_PER_FILE = 25 * 1024 * 1024;
export const SIGNATURE_MAX_FILES = 20;
export const SCELLEMENT_MAX_FILES = 50;
export const MAX_FILE_NAME_LENGTH = 100;

export const MAX_SIGNATURE_MAIL_SIZE = 65535;

export const PERSONAL_SIGNATURE_IMAGE_RANKS = [1, 2, 3];
export const HANDWRITTEN_SIGNATURE_RANKS = [4];
export const SIGNATURE_TEXT_RANKS = [5];

export const HANDWRITTEN_CANVAS_PREVIEW_WIDTH = 750;
export const HANDWRITTEN_CANVAS_PREVIEW_HEIGHT = 180;
export const TEXT_PICTURE_CANVAS_PREVIEW_WIDTH = 300;
export const TEXT_PICTURE_CANVAS_PREVIEW_HEIGHT = 72;
export const PNG_CONTENT_TYPE = "image/png";

export const OFFERED_SIGNATURE_TEXT_FONTS = [
  "Poppins-Light",
  "Caveat-Regular",
  "KleeOne-SemiBold",
];

export const MAX_LENGTH_TRANSACTION_MODEL_NAME = 45;

export const LIMIT_CREDIT_WARNING_VALUE = 5000;

export const SHERLOCKS_URL = {
  REQUEST: "/sherlocks/v2/payment/request",
};

export const CGU = {
  LINK: "https://ressources.ecma-solutions.com/plezi_file/647ef429d59cbc0739f30024/cgu_jesignexpert.com.pdf",
  LABEL: "conditions générales d'utilisation",
};

export const DPP = {
  LINK: "https://ressources.ecma-solutions.com/plezi_file/64e8e8fed59cbc0747fe71c7/annexe-cgu-protection-des-donnees-personnelles-jesignexpert.pdf",
  LABEL: "politique de protection des données",
};

export const ECMA_LINK =
  "https://ecma-solutions.com/produits-expert-comptable/jesignexpert";

export const ECMA_DELEGATION_INFO_LINK =
  "https://ecma-solutions.com/actualites/comptexpert-systeme-authentification-ordre-experts-comptables";

/* eslint-disable @typescript-eslint/naming-convention */
export const ONBOARDING_ERROR_ENUM = {
  NEW_EMAIL: "NEW_EMAIL",
  ACCOUNT_DELETED: "ACCOUNT_DELETED",
  NOT_IN_ORGANIZATION: "NOT_IN_ORGANIZATION",
  CERTIFICATE_INVALID: "CERTIFICATE_INVALID",
};
/* eslint-enable @typescript-eslint/naming-convention */

/* eslint-disable @typescript-eslint/naming-convention */
export const CERTIFICATE_REVOKE_REASON = {
  CERTIFICATE_REVOKED_INFORMATION: "CERTIFICATE_REVOKED_INFORMATION",
  CERTIFICATE_REVOKED_MOBILE: "CERTIFICATE_REVOKED_MOBILE",
  CERTIFICATE_REVOKED_RENEW: "CERTIFICATE_REVOKED_RENEW",
  CERTIFICATE_REVOKED_BUTTON: "CERTIFICATE_REVOKED_BUTTON",
};
/* eslint-enable @typescript-eslint/naming-convention */

export const GUESTS_DELEGATION_SELECTION = [
  { value: "preparation_electronique", label: "Délégation de préparation" },
  { value: "lancement_electronique", label: "Délégation de lancement" },
  { value: "scellement_electronique", label: "Délégation de scellement" },
  { value: "signature_electronique", label: "Délégation de signature" },
];

export const SOMME_QCP_CERTIFICATE_CREATION_IN_CENTS = 2000;

export const SIGNATURE_LEVEL_TIERS: {
  level: TiersSignatureLevelAvailable;
  label: string;
}[] = [
  {
    level: SignatureLevelAvailable.LEVEL_1,
    label: "Niv 1 : simple",
  },
  {
    level: SignatureLevelAvailable.LEVEL_2,
    label: "Niv 2 : avancée",
  },
];

export const SIGNATURE_LEVEL: {
  level: QcpSignatureLevelAvailable;
  label: string;
}[] = [
  {
    level: SignatureLevelAvailable.LEVEL_2,
    label: "Niv 2 : avancée",
  },
  {
    level: SignatureLevelAvailable.LEVEL_3,
    label: "Niv 3 : avancée basée sur un certificat qualifié",
  },
];

export const REVOKE_QCP_HELP_LINK =
  "https://assistance.jesignexpert.com/hc/fr/articles/14733437354012-Comment-r%C3%A9voquer-son-certificat-qualifi%C3%A9-QCP";

export type AlertDetails = {
  key: string;
  instantEmailPref: boolean;
  delayType: AlertPreferenceDelayTypeEnum;
  multipleThreshold?: boolean;
  notificationClassicConfiguration: boolean;
};

export const ALERT_OBJECT_ENUM: Record<AlertPreferencesType, AlertDetails> = {
  [AlertPreferencesType.COLLECTION_PREPARED]: {
    key: "collectionPrepared",
    instantEmailPref: true,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.TRANSACTION_SENT]: {
    key: "transactionSent",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.SIGNATURE_PERFORMED]: {
    key: "signaturePerformed",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.COLLECTION_PAUSED]: {
    key: "collectionPaused",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.DAYS_PASSED,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.EXPECTED_EXPIRATION]: {
    key: "expectedExpiration",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.DAYS_EXACTLY,
    notificationClassicConfiguration: true,
    multipleThreshold: true,
  },
  [AlertPreferencesType.COLLECTION_EXPIRED]: {
    key: "collectionExpired",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.SIGNATURES_COLLECTION_DONE]: {
    key: "signaturesCollectionDone",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.SIGNATURE_REFUSED]: {
    key: "signatureRefused",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.SEALING_COLLECTION_DONE]: {
    key: "sealingCollectionDone",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.SEALING_ERROR]: {
    key: "sealingError",
    instantEmailPref: true,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.EXPECTED_CLEAN_UP]: {
    key: "expectedCleanUp",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.DAYS_LEFT,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.NOT_DOWNLOADED_DOCUMENT]: {
    key: "notDownloadedDocument",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.DAYS_LEFT,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.LOW_ACCOUNT]: {
    key: "lowAccount",
    instantEmailPref: true,
    delayType: AlertPreferenceDelayTypeEnum.AMOUNT,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.ACCOUNT_CREDITED]: {
    key: "accountCredited",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.CONFIDENTIALITY_PROPERTY_REMOVED]: {
    key: "confidentialityPropertyRemoved",
    instantEmailPref: true,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: true,
  },
  [AlertPreferencesType.COLLECTION_RELAUNCHED]: {
    key: "collectionRelaunched",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.BILL_SENT]: {
    key: "billSent",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.MERGED]: {
    key: "merged",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.MIGRATION]: {
    key: "migration",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.DEGRADED_MODE]: {
    key: "degradedMode",
    instantEmailPref: true,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
  [AlertPreferencesType.UNKNOWN]: {
    key: "unknown",
    instantEmailPref: false,
    delayType: AlertPreferenceDelayTypeEnum.NOTHING,
    notificationClassicConfiguration: false,
  },
};

export const CONSEIL_EXPERT_COMPTABLES =
  "CONSEIL NATIONAL DE L'ORDRE DES EXPERTS-COMPTABLES";

export const DEFAULT_INITIAL_FIELDS_ALIGNMENT: InitialFieldsAlignment = "right";
export const DEFAULT_INITIAL_FIELDS_Y_POSITION: number = 50;

export const MAX_SIGNATORIES = 50;

export const ONBOARDING_NEW_INFORMATION_TIMEOUT_MINUTES = 5 * 60 * 1000; // FIVE MINUTES

export const DEFAULT_PAGE_SIZE = 10;

export const PAGINATION_SIZE_ENUM = [5, 10, 15, 20, 30, 40, 50];

export const DASHBOARD_EXPORT_FILENAME =
  "jesignexpert_export_transactions.xlsx";

export const CONTACT_EXPORT_FILENAME = "export_contacts.xlsx";

export const CGU_TYPES = {
  CGU: "cgu",
  DPP: "dpp",
};

export const DEFAULT_SIGNATURE_REQUIREMENT_MODE: SignatureRequirementMode =
  "ALL";

export const REFETCH_TIMEOUT = 2000;

export const WS_TIMEOUT = 45000;

export const WS_UPLOAD_TIMEOUT = 120000;

export const EMAIL_FOR_SEALING = "scellement";
